import { FC, PropsWithChildren, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';

import { useIsRefreshingSession } from '@/api/bff/auth/use-refresh-session';
import { useToast } from '@/shared/design-system';
import { GeneralLoadingFallback } from '@/shared/modules/general-loading-fallback';
import { Permission, usePermissions } from '@/shared/modules/permissions';

import { PagePaths } from './page-paths';

/**
 * Deliberately says nothing about which permission or which page was refused —
 * a denial must not tell the caller what exists behind it.
 */
export const ROUTE_ACCESS_DENIED_MESSAGE = "You don't have access to that page";

export type RequirePermissionProps = PropsWithChildren<{
  /** Permission the session must hold to reach this route; arrays are OR requirements. */
  permission: Permission | readonly Permission[];
}>;

/**
 * Route-level permission gate: renders `children` only when the session holds
 * `permission`, otherwise redirects to the root and explains why with a toast.
 *
 * Composes with `ProtectedRoute` rather than replacing it — authentication is
 * still checked first, so an unauthenticated visitor is bounced by
 * `ProtectedRoute` and never sees an authorization toast.
 *
 * `usePermissions()` is used instead of `useCan()` because `useCan()` cannot
 * distinguish "denied" from "not known yet" — both are `false` — and redirecting
 * on the latter would bounce legitimate users on first paint.
 *
 * UX affordance only: the backend enforces every permission independently and
 * still returns 403 for the data behind the page.
 */
export const RequirePermission: FC<RequirePermissionProps> = ({ permission, children }) => {
  const { has, isLoading } = usePermissions();
  const isRefreshingSession = useIsRefreshingSession();
  const allowed =
    typeof permission === 'string'
      ? has(permission)
      : permission.some((requiredPermission) => has(requiredPermission));
  // The permission list is unknown while loading, so a denial is only real once
  // it has resolved. On error it resolves to an empty list — deny by default.
  const isDenied = !isRefreshingSession && !isLoading && !allowed;
  const hasNotifiedRef = useRef(false);
  const { add: addToast } = useToast();

  useEffect(() => {
    if (!isDenied || hasNotifiedRef.current) return;

    // Guarded by a ref, not by the effect deps: React StrictMode runs setup →
    // cleanup → setup on the same fiber, and the ref survives that remount, so
    // the toast fires exactly once. A genuinely new navigation to a gated URL
    // mounts a new instance with a fresh ref and does toast again.
    hasNotifiedRef.current = true;
    addToast({ title: ROUTE_ACCESS_DENIED_MESSAGE, data: { status: 'error' } });
  }, [isDenied, addToast]);

  if (allowed) return children;

  if (isRefreshingSession || isLoading) {
    return <GeneralLoadingFallback />;
  }

  if (!allowed) {
    return <Navigate to={PagePaths.root} replace />;
  }

  return children;
};
